import { consumer, IResponse } from '@bemap/utils';

export interface User {
  id: number;
  name: string;
  email: string;
  active: boolean;
  modules?: Record<string, boolean>;
}

export interface ModuleInfo {
  id: string;
  label: string;
  description: string;
  hasAccess: boolean;
}


interface ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
}

export class UserModulesService {
  static async getUsers(): Promise<IResponse> {
    const url = `/users`;

    try {
      return await consumer('GET', url);
    } catch (error: unknown) {
      const apiError = error as ApiError;
      const msg = apiError.response?.data?.message || 'Failed to load users';
      throw {
        statusCode: apiError.response?.status || 500,
        message: msg
      };
    }
  }

  static async getUserById(id: number): Promise<IResponse> {
    const url = `/users/${id}`;

    try {
      return await consumer('GET', url);
    } catch (error: unknown) {
      const apiError = error as ApiError;
      const msg = apiError.response?.data?.message || 'Failed to load user';
      return {
        statusCode: apiError.response?.status || 500,
        message: msg
      };
    }
  }

  static async createUser(user: Omit<User, 'id'>): Promise<IResponse> {
    const url = `/users`;

    try {
      return await consumer('POST', url, user);

    } catch (error: unknown) {
      const apiError = error as ApiError;
      const msg = apiError.response?.data?.message || 'Failed to create user';
      throw {
        statusCode: apiError.response?.status || 500,
        message: msg
      };
    }
  }

  static async updateUser(user: User): Promise<IResponse> {
    const url = `/users/${user.id}`;

    try {
      return await consumer('PATCH', url, user);

    } catch (error: unknown) {
      const apiError = error as ApiError;
      const msg = apiError.response?.data?.message || 'Failed to update user';
      throw {
        statusCode: apiError.response?.status || 500,
        message: msg
      };
    }
  }

  static async deleteUser(id: number): Promise<IResponse> {
    const url = `/users/${id}`;

    try {
      return await consumer('DELETE', url);
    } catch (error: unknown) {
      const apiError = error as ApiError;
      const msg = apiError.response?.data?.message || 'Failed to delete user';
      throw {
        statusCode: apiError.response?.status || 500,
        message: msg
      };
    }
  }

  static async getUserModules(userId: number): Promise<IResponse> {
    const url = `/users/${userId}/modules`;

    try {
      return await consumer('GET', url);
    } catch (error: unknown) {
      const apiError = error as ApiError;
      const msg = apiError.response?.data?.message || 'Failed to load user modules';
      throw {
        statusCode: apiError.response?.status || 500,
        message: msg
      };
    }
  }

  static async updateUserModules(userId: number, modules: Record<string, boolean>): Promise<IResponse> {
    const url = `/users/${userId}/modules`;

    try {
      return await consumer('PUT', url, { modules });

    } catch (error: unknown) {
      const apiError = error as ApiError;
      const msg = apiError.response?.data?.message || 'Failed to update user modules';
      throw {
        statusCode: apiError.response?.status || 500,
        message: msg
      };
    }
  }

  static async checkModuleAccess(userId: number, moduleId: string): Promise<IResponse> {
    const url = `/users/${userId}/modules/check/${moduleId}`;

    try {
      return await consumer('GET', url);
    } catch (error: unknown) {
      const apiError = error as ApiError;
      const msg = apiError.response?.data?.message || 'Failed to check module access';
      throw {
        statusCode: apiError.response?.status || 500,
        message: msg
      };
    }
  }

  static async toggleUserStatus(active: boolean, userId: number): Promise<IResponse> {
    const url = `/users/${userId}`;
    const body = {
      status: active ? 1 : 0,
    };

    try {
      return await consumer('PATCH', url, body);

    } catch (error: unknown) {
      const apiError = error as ApiError;
      const msg = apiError.response?.data?.message || 'Failed to update user status';
      throw {
        statusCode: apiError.response?.status || 500,
        message: msg
      };
    }
  }
} 